/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Box, Grid, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { InactivityHandler } from "@components/InactivityHandler";
import CustomRightImage from "@components/CustomRightImagev2";
import { useLazyGetPersonByIdQuery, useLazyGetPersonsQuery } from "@api/personasApi";
import { useLazyGetTabsConfigQuery } from "@api/catalogs";
import { BOFU_TABS_CONFIG } from "@helpers/constantes";
import DynamicPersonForm from "@components/PersonForm/DynamicPersonForm";
import { findPersonasTab, resolvePersonFormsConfig } from "@components/PersonForm/personHelpers";
import { ApiError, PersonaStructure, PersonFormsConfig } from "@interfaces/response";
import { setModalErrorVisible } from "@store/slices/appSlice";
import * as Sentry from "@sentry/react";

const HUB_URL = BOFU_TABS_CONFIG.tab_personas.url;

const FormularioPersonaDetalle: React.FC = () => {
  const { personId } = useParams<{ personId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  const application = useSelector((state: RootState) => state.register.application);

  const [person, setPerson] = useState<PersonaStructure | null>(null);
  const [allPersons, setAllPersons] = useState<PersonaStructure[]>([]);
  const [formsConfig, setFormsConfig] = useState<PersonFormsConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [getPersonById] = useLazyGetPersonByIdQuery();
  const [getPersons] = useLazyGetPersonsQuery();
  const [getTabsConfig] = useLazyGetTabsConfigQuery();

  const goBack = () => {
    navigate(HUB_URL);
  };

  const loadData = async () => {
    if (!personId || !application?.id || !application?.type) {
      goBack();
      return;
    }

    try {
      setIsLoading(true);

      const [personData, personsList, tabsConfig] = await Promise.all([
        getPersonById(personId).unwrap(),
        getPersons({ loanApplicationId: application.id }).unwrap(),
        getTabsConfig({
          taxcode: application.type,
          code: BOFU_TABS_CONFIG.tab_personas.code,
        }).unwrap(),
      ]);

      if (!personData) {
        goBack();
        return;
      }

      setPerson(personData);
      setAllPersons(personsList || []);
      setFormsConfig(resolvePersonFormsConfig(findPersonasTab(tabsConfig)?.forms));
    } catch (error) {
      const apiError = error as ApiError;
      Sentry.captureException(error, {
        extra: { component: "FormularioPersonaDetalle-loadData" },
      });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
      goBack();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [personId, application?.id, application?.type]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <Box textAlign="center" mx={4} pb={4}>
          <InactivityHandler />

          {isLoading || !person || !formsConfig ? (
            <Box mt={3}>
              <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
            </Box>
          ) : (
            <Box mt={3}>
              <Typography
                textAlign="center"
                variant={isMobileOrTablet ? "body2" : "body1"}
                fontWeight={700}
                color="primary.dark"
              >
                Información de la persona
              </Typography>

              <DynamicPersonForm
                key={person.id}
                person={person}
                formsConfig={formsConfig}
                allPersons={allPersons}
                onSaved={goBack}
                onBack={goBack}
                hideTitle
              />
            </Box>
          )}
        </Box>
      </Grid>

      <Grid item md={4} sx={{ display: { xs: "none", md: "block" } }}>
        <CustomRightImage />
      </Grid>
    </Grid>
  );
};

export default FormularioPersonaDetalle;
